import type { LocationStatus, WritableLocationStatus } from '../../interfaces/IScheduler.js';

// Location-status maps for the area board + legend — the web drawer's and the
// mobile task sheet's. Named with the LOCATION_ prefix so they don't shadow the
// different TASK-status maps (`STATUS_KEY`).
//
// What a tap on an area does is `nextAreaAction`, shared with the server in
// `area-status`; re-exported here beside the labels the boards read with it.
export { areaReportTier, nextAreaAction, type AreaAction, type AreaReportTier } from '../../../area-status/index.js';

/** i18n key for each location status label. */
export const LOCATION_STATUS_LABEL_KEY: Record<LocationStatus, string> = {
  unplanned: 'scheduler.panel.locations.statusUnplanned',
  planned: 'scheduler.panel.locations.statusPlanned',
  inProgress: 'scheduler.panel.locations.statusInProgress',
  delayed: 'scheduler.panel.locations.statusDelayed',
  delayedOverdue: 'scheduler.panel.locations.statusDelayedOverdue',
  overdue: 'scheduler.panel.locations.statusOverdue',
  readyForReview: 'scheduler.panel.locations.statusReadyForReview',
  complete: 'scheduler.panel.locations.statusCompleted',
};

/** The area-status palette both boards paint from — each app maps a tone to its
 *  own colours (the web's CSS class, the phone's pale tint and ink). Nothing
 *  promised yet looks planned; never started AND past its date looks overdue —
 *  the date is what the board marks. */
export type AreaTone = 'planned' | 'progress' | 'delayed' | 'overdue' | 'review' | 'done';

export const AREA_TONE: Record<LocationStatus, AreaTone> = {
  unplanned: 'planned',
  planned: 'planned',
  inProgress: 'progress',
  delayed: 'delayed',
  delayedOverdue: 'overdue',
  overdue: 'overdue',
  readyForReview: 'review',
  complete: 'done',
};

/** The mark an area status draws: a ring (nothing reported), a half-filled disc
 *  (under way, delayed or awaiting review), a flag (overdue — it shares
 *  delayed's red and is told apart by the glyph) or a check (done). */
export type AreaMark = 'check' | 'flag' | 'half' | 'ring';

export const AREA_MARK: Record<LocationStatus, AreaMark> = {
  unplanned: 'ring',
  planned: 'ring',
  inProgress: 'half',
  delayed: 'half',
  delayedOverdue: 'flag',
  overdue: 'flag',
  readyForReview: 'half',
  complete: 'check',
};

/** The marks' geometry — one 14×14 frame, drawn once for both apps; each
 *  renderer sets its own stroke weights. */
export const STATUS_GLYPH_VIEWBOX = '0 0 14 14';
export const STATUS_GLYPH_RING = { cx: 7, cy: 7, r: 4.4 } as const;
export const STATUS_GLYPH_PATHS = {
  check: 'M2.7 7.3 L5.7 10.3 L11.2 4.1',
  /** The ring's right half, filled. */
  halfFill: 'M7 2.6 A4.4 4.4 0 0 1 7 11.4 Z',
  flagPole: 'M3.4 12.2 V2.2',
  flag: 'M3.4 2.6 H10.8 L9.2 5.2 L10.8 7.8 H3.4 Z',
} as const;

/** The area meter's order — reads like a progress bar: what is done fills from
 *  the left, what is still open trails to the right, the late readings between
 *  them so the yellow→orange→red escalation sits next to the work it
 *  describes. */
export const AREA_METER_ORDER: readonly LocationStatus[] = ['complete', 'readyForReview', 'inProgress', 'delayed', 'overdue', 'planned'];

/** The meter segment an area counts in. `delayedOverdue` counts as OVERDUE:
 *  what the two share is that the date has passed, which is what the flag says
 *  — the same area must not read "overdue" on its cell and "delayed" on the
 *  meter. */
export function meterStatusOf(status: LocationStatus): LocationStatus {
  return status === 'delayedOverdue' ? 'overdue' : status;
}

/** The meter's segments over these area statuses: one per status the areas
 *  are in, in {@link AREA_METER_ORDER}, with its count — never a zero. */
export function meterSegments(statuses: readonly LocationStatus[]): { status: LocationStatus; count: number }[] {
  const counts = new Map<LocationStatus, number>();
  for (const status of statuses.map(meterStatusOf)) counts.set(status, (counts.get(status) ?? 0) + 1);
  return AREA_METER_ORDER.flatMap((status) => {
    const count = counts.get(status);
    return count === undefined ? [] : [{ status, count }];
  });
}

/** What a whole-floor press writes: the floor done — or, on a floor already
 *  all done, that taken back. */
export function floorReportTarget(statuses: readonly LocationStatus[]): WritableLocationStatus {
  return statuses.every((status) => status === 'complete') ? 'planned' : 'complete';
}
